import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@/components/Card/Card';

import styles from './ClassStatsSummary.module.css';

interface ClassStatsSummaryProps {
  classAveragePreLectureScore?: number;
  classAverageScore: number;
  maxScore: number;
  questionMaxScores: number[];
  questionScores: number[];
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function clampPercentage(value: number) {
  return Math.max(0, Math.min(100, value));
}

export function ClassStatsSummary({
  classAveragePreLectureScore,
  classAverageScore,
  maxScore,
  questionMaxScores,
  questionScores,
}: ClassStatsSummaryProps) {
  const [showPoints, setShowPoints] = useState(false);

  const percentage = maxScore > 0 ? clampPercentage((classAverageScore / maxScore) * 100) : 0;
  const chartData = useMemo(
    () =>
      questionMaxScores.map((questionMaxScore, index) => {
        const score = questionScores[index] ?? 0;

        return {
          label: `Q${index + 1}`,
          percentage:
            questionMaxScore > 0
              ? clampPercentage((score / questionMaxScore) * 100)
              : 0,
          points: score,
        };
      }),
    [questionMaxScores, questionScores],
  );
  const maxQuestionScore = questionMaxScores.reduce(
    (largest, score) => Math.max(largest, score),
    0,
  );
  const chartSummary = chartData.length
    ? chartData
        .map((entry, index) =>
          showPoints
            ? `${entry.label}: ${formatScore(entry.points)} of ${formatScore(questionMaxScores[index] ?? 0)} points`
            : `${entry.label}: ${formatScore(entry.percentage)} percent`,
        )
        .join('. ')
    : 'No question score data is available yet.';

  if (maxScore <= 0 || chartData.length === 0) {
    return null;
  }

  return (
    <Card
      aria-labelledby="class-stats-heading"
      as="section"
      className={styles.card}
      variant="metric"
    >
      <div className={styles.header}>
        <p className={styles.eyebrow}>Class Stats</p>
        <h2 className={styles.title} id="class-stats-heading">
          Class Average: {formatScore(classAverageScore)} / {formatScore(maxScore)} ({formatScore(percentage)}%)
        </h2>
        {classAveragePreLectureScore !== undefined && classAveragePreLectureScore > 0 ? (
          <p className={styles.supporting}>
            Pre-lecture average: {formatScore(classAveragePreLectureScore)}
          </p>
        ) : null}
      </div>

      <label className={styles.toggle}>
        <input
          checked={showPoints}
          onChange={(event) => setShowPoints(event.target.checked)}
          type="checkbox"
        />
        <span>Show question score in points (not percentage)</span>
      </label>

      <div
        aria-label={`Average score per question. ${chartSummary}`}
        className={styles.chartCard}
        role="img"
      >
        <p className={styles.chartTitle}>Average Score per Question</p>
        <div className={styles.srOnly}>{chartSummary}</div>

        <ResponsiveContainer height={260} width="100%">
          <BarChart data={chartData} margin={{ left: 0, right: 16, top: 12, bottom: 0 }}>
            <CartesianGrid stroke="var(--color-border-light)" vertical={false} />
            <XAxis dataKey="label" />
            <YAxis
              allowDecimals
              domain={
                showPoints
                  ? [0, Math.max(1, maxQuestionScore)]
                  : [0, 100]
              }
            />
            <Bar
              dataKey={showPoints ? 'points' : 'percentage'}
              fill="var(--color-accent)"
              radius={[12, 12, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
